import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FormFieldAsyncCombobox, FormFieldSelect } from '@/shared/components/custom/form-field';
import { useAddProjectMember, useProjectMembers } from '@/features/projects/public';
import { searchOrgMembers } from '../../api/orgMembersSearch.service';
import { addProjectMemberSchema, type AddProjectMemberInput } from '../../schemas';
import { MemberRole, ASSIGNABLE_PROJECT_ROLES } from '@/core/constants/roles';

interface AddProjectMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
}

export function AddProjectMemberDialog({
  open,
  onOpenChange,
  projectId,
}: AddProjectMemberDialogProps) {
  const { t } = useTranslation('tasks');
  const { mutate: addMember, isPending } = useAddProjectMember(projectId);
  const { data: members = [] } = useProjectMembers(projectId);
  const existingUserIds = new Set(members.map((m) => m.userId));
  const existingUserIdsKey = members
    .map((m) => m.userId)
    .sort()
    .join(',');

  async function fetchAvailableMembers(params: { search: string; pageSize: number }) {
    const result = await searchOrgMembers(params);
    return {
      ...result,
      items: result.items.filter((item) => !existingUserIds.has(item.id)),
    };
  }

  const form = useForm<AddProjectMemberInput>({
    resolver: zodResolver(addProjectMemberSchema),
    defaultValues: {
      userId: '',
      role: MemberRole.Collaborator,
    },
  });

  function handleSubmit(data: AddProjectMemberInput) {
    addMember(
      { userId: data.userId, role: data.role },
      {
        onSuccess: () => {
          form.reset();
          onOpenChange(false);
        },
      },
    );
  }

  function handleOpenChange(value: boolean) {
    if (!value) form.reset();
    onOpenChange(value);
  }

  const roleOptions = ASSIGNABLE_PROJECT_ROLES.map((r) => ({
    value: r,
    label: t(`header.roles.${r}`),
  }));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="tasks-add-member-dialog-root" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('header.addMember.title')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4 py-2">
          <FormFieldAsyncCombobox
            control={form.control}
            name="userId"
            label={t('header.addMember.memberLabel')}
            queryKey={['org-members-search', projectId, existingUserIdsKey]}
            fetchFn={fetchAvailableMembers}
            floatingLabel
            clearable
            placeholder={t('header.addMember.memberPlaceholder')}
            data-testid="tasks-add-member-dialog-combobox"
          />

          <FormFieldSelect
            control={form.control}
            name="role"
            label={t('header.addMember.roleLabel')}
            options={roleOptions}
            floatingLabel
            data-testid="tasks-add-member-dialog-role-select"
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('subtasks.confirmDelete.cancel')}
            </Button>
            <Button type="submit" disabled={isPending} data-testid="tasks-add-member-dialog-submit">
              {t('header.addMember.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
